import { skipToken } from "@reduxjs/toolkit/query";
import {
  AiCreditBalanceLevel,
  type AiCreditBalance,
} from "@repo/app-types/entities/creditUsage";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { AI_CREDITS_USED_UP_MESSAGE } from "@repo/app-types/helpers/billing/aiCredits";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { useAiCreditStatusQuery } from "../../api/entities/company.js";
import { appPaths } from "../../app/appPaths.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";

/** Where a company asks to add AI credits; they are never self-serve. */
export const AI_CREDITS_CONTACT_HREF =
  "mailto:support@alpharun.com?subject=Purchase%20AI%20credits";

/** How often a mounted warning re-reads the AI credit status. */
export const AI_CREDIT_STATUS_POLLING_MS = 10 * 60_000;

const DISMISSED_LEVEL_STORAGE_KEY = "admin.aiCreditsLimitBanner.dismissedLevel";

export type AiCreditsWarning = Exclude<
  AiCreditBalance,
  { level: typeof AiCreditBalanceLevel.Available }
>;

export type AiCreditsWarningLevel = AiCreditsWarning["level"];

const linkClassName =
  "font-semibold whitespace-nowrap underline underline-offset-2 hover:no-underline";

interface AiCreditsLimitBannerViewProps {
  warning: AiCreditsWarning;
  onDismiss: () => void;
}

/** The app-wide notice that the company's AI credits are low or used up. */
export const AiCreditsLimitBannerView: FC<AiCreditsLimitBannerViewProps> = (
  props,
) => {
  const { warning, onDismiss } = props;
  const { iconClassName, headline, detail } = describeWarning(warning);

  return (
    <div
      className="relative flex min-h-9 w-full items-start justify-start bg-gray-700 py-2 pr-12 pl-4 text-left text-sm leading-5 text-white sm:justify-center sm:px-12"
      role="status"
    >
      <div className="flex min-w-0 items-start gap-2">
        <IconAlertTriangle
          aria-hidden="true"
          className={twMerge("size-5 shrink-0", iconClassName)}
        />
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-pretty">
            <span className="font-semibold">{headline}</span> {detail}
          </p>
          <div className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1">
            <Link
              className={linkClassName}
              to={appPaths.settingsBilling.build()}
            >
              View usage
            </Link>
            <span aria-hidden="true" className="text-white/50">
              ·
            </span>
            <a className={linkClassName} href={AI_CREDITS_CONTACT_HREF}>
              Contact us to add credits
            </a>
          </div>
        </div>
      </div>
      <IconButton
        accessibleLabel="Dismiss AI credit notice"
        className="absolute top-1 right-2 text-white/70 hover:bg-white/10 hover:text-white"
        icon={<IconX aria-hidden="true" className="size-4" />}
        onClick={onDismiss}
        size={Size.SMALL}
        variant={ButtonVariant.Tertiary}
      />
    </div>
  );
};

function describeWarning(warning: AiCreditsWarning): {
  iconClassName: string;
  headline: string;
  detail: string;
} {
  switch (warning.level) {
    case AiCreditBalanceLevel.RunningLow:
      return {
        iconClassName: "text-amber-400",
        headline: `${warning.percent_used}% of AI credits used.`,
        detail:
          "Ask Alpharun, Automations, and other credit-based features pause at 100%.",
      };
    case AiCreditBalanceLevel.Exhausted:
      return {
        iconClassName: "text-red-400",
        headline: `${AI_CREDITS_USED_UP_MESSAGE}.`,
        detail:
          "Ask Alpharun, Automations, and other credit-based features are paused.",
      };
  }
}

/**
 * The limit banner for billing editors. Dismissing it hides that level for the
 * browser session; reaching the next level shows it again.
 */
export const AiCreditsLimitBanner: FC = () => {
  const authorization = useTeammateAuthorization();
  const canManageBilling = authorization.can(
    PermissionActions.UPDATE,
    PermissionSubjects.BILLING,
  );
  const { data: status } = useAiCreditStatusQuery(
    canManageBilling ? undefined : skipToken,
    { pollingInterval: AI_CREDIT_STATUS_POLLING_MS },
  );
  const [dismissedLevel, setDismissedLevel] = useState(readDismissedLevel);

  if (!status) {
    return null;
  }
  if (
    status.level === AiCreditBalanceLevel.Available ||
    status.level === dismissedLevel
  ) {
    return null;
  }

  return (
    <AiCreditsLimitBannerView
      warning={status}
      onDismiss={() => {
        storeDismissedLevel(status.level);
        setDismissedLevel(status.level);
      }}
    />
  );
};

function readDismissedLevel(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISSED_LEVEL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeDismissedLevel(level: AiCreditsWarningLevel): void {
  try {
    window.sessionStorage.setItem(DISMISSED_LEVEL_STORAGE_KEY, level);
  } catch {
    // Storage can be unavailable (e.g. private browsing); state still hides it.
  }
}
